import { useMutation } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';

/**
 * Re-send the invite email for a Staff member whose account hasn't been
 * activated yet (PROD-789 follow-up).
 *
 * Endpoint: `POST /api/Account/ReSendInvite`
 *
 * **NOT `/api/Staff/ResendStaffInvite`** — the prior stub in
 * `staff-account-tab.tsx:189` guessed wrong before the cURL landed.
 * Captured against working dev cURL 2026-05-25.
 *
 * **`user` field is a discriminator literal** that the backend reads to
 * route the invite template + audit-log entry. Verified value: `"Staff"`.
 * For practitioner re-invites the value is presumably `"Doctor"` (Flutter
 * `app_constants.dart` typically defines both), but practitioner re-invite
 * isn't in scope for this PR — leaving the hook signature open so a
 * future caller can pass `user: "Doctor"` without touching this file.
 *
 * **Why `organizationId` is on the body when it's also a header**: the
 * backend reads it from both — header for the standard apiClient
 * gRPC-metadata injection, body for the invite-template logic that needs
 * the org name at template-render time. Ship both; cURL confirms the
 * backend is happy.
 *
 * **Why `firstName` is on the body**: invite email personalisation
 * ("Hi Adam, you've been invited to..."). The cURL ships it explicitly
 * rather than letting the backend resolve from `emailAddress`, so the
 * React form must provide it from the draft.
 */
export interface ResendInviteBody {
  emailAddress: string;
  organizationId: string;
  firstName: string;
  /**
   * Discriminator literal — `"Staff"` for nurse-staff re-invites.
   * `"Doctor"` likely supported but not exercised by this PR.
   */
  user: 'Staff' | 'Doctor';
}

export function useResendInvite() {
  return useMutation({
    mutationFn: (body: ResendInviteBody) =>
      post<unknown>('/api/Account/ReSendInvite', body),
  });
}
